/**
 * A linha do tempo da aba "Histórico" (24/09/2026; tudo da oportunidade desde
 * 23/10/2026): os passos do funil e o que aconteceu dentro da oportunidade
 * (nota, documento, consulta, edição), juntos, do mais recente ao mais
 * antigo. Sem React, para o teste não precisar de tela.
 *
 * ── "Pelo CRM ou por automação" é uma resposta, e não um vazio ───────────
 *
 * Quem moveu a etapa só é conhecido quando o movimento passou pelo CliqCRM. O
 * feito pela regra automática aparece dizendo isso, em vez de uma linha sem
 * autor que pareceria defeito da tela. Nas atividades, sem nome é o sistema.
 *
 * ── Nota e documento apagados dizem que foram apagados ───────────────────
 *
 * O trecho da nota e o nome do documento são os de hoje: apagados, a API os
 * manda vazios, e a linha diz isso em vez de ficar sem explicação.
 */

import type {
  AtendimentoDoSisac,
  AtividadeDaOportunidade,
  MudancaNaOportunidade,
  PassoNoFunil,
} from '@cliqcrm/contracts';
import { nomeDaEtapa } from '@/entities/comercial/etapa';
import { nomeDoAtendimento, quandoNaClinica, valorEhPrevisto } from '@/entities/saude/atendimento';
import { dinheiro, dinheiroComCentavos } from '@/shared/lib/formato';

export type GrupoDoHistorico = 'etapa' | 'nota' | 'documento' | 'consulta' | 'edicao';

export interface EntradaDoHistorico {
  readonly chave: string;
  readonly quando: string;
  readonly grupo: GrupoDoHistorico;
  readonly frase: string;
  /** As linhas de apoio: o trecho da nota, o motivo da perda, o que a edição mudou. */
  readonly detalhes: readonly string[];
  /** "por Fulano", ou quem fez quando não foi uma pessoa da equipe. */
  readonly por: string;
  readonly resultado: 'ganho' | 'perdido' | null;
}

const NOME_DO_CAMPO_DA_CONSULTA = {
  medico: 'médico',
  especialidade: 'especialidade',
  horario: 'horário',
};

/** "a", "a e b", "a, b e c". */
function juntar(itens: readonly string[]): string {
  if (itens.length <= 1) return itens[0] ?? '';
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** A previsão de fechamento é uma data (meia-noite em UTC): lida em UTC, não muda de dia. */
function data(instante: string | null): string {
  return instante
    ? new Date(instante).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : 'sem previsão';
}

/** Uma linha por campo que a edição mudou. */
export function linhaDaMudanca(mudanca: MudancaNaOportunidade): string {
  switch (mudanca.campo) {
    case 'titulo':
      return 'Título alterado';
    case 'valor':
      return `Valor: ${dinheiro(mudanca.antes) ?? 'sem valor'} → ${dinheiro(mudanca.depois) ?? 'sem valor'}`;
    case 'fechamento_previsto':
      return `Previsão de fechamento: ${data(mudanca.antes)} → ${data(mudanca.depois)}`;
    case 'cliente':
      return 'Cliente trocado';
  }
}

function entradaDoPasso(passo: PassoNoFunil, i: number): EntradaDoHistorico {
  const para = nomeDaEtapa(passo.para.rotulo);
  return {
    chave: `passo-${passo.quando}-${i}`,
    quando: passo.quando,
    grupo: 'etapa',
    frase:
      passo.tipo === 'criada'
        ? `Entrou no funil em ${para}`
        : `De ${nomeDaEtapa(passo.de?.rotulo ?? '')} para ${para}`,
    detalhes: passo.perda
      ? [
          `Motivo: ${passo.perda.motivo ?? 'não informado'}${
            passo.perda.observacao ? ` · ${passo.perda.observacao}` : ''
          }`,
        ]
      : [],
    por:
      passo.origem === 'pessoa'
        ? `por ${passo.por ?? 'alguém da equipe'}`
        : 'pelo CRM ou por automação',
    resultado: passo.resultado,
  };
}

function fraseDoDocumento(
  atividade: Extract<AtividadeDaOportunidade, { tipo: 'documento' }>,
): Pick<EntradaDoHistorico, 'frase' | 'detalhes'> {
  if (atividade.acao === 'removido') {
    // Removido, o documento não existe mais, e o nome não volta.
    return { frase: 'Documento removido', detalhes: [] };
  }
  return atividade.nome
    ? { frase: `Documento adicionado: ${atividade.nome}`, detalhes: [] }
    : { frase: 'Documento adicionado', detalhes: ['O documento foi removido depois.'] };
}

/** Um `switch` sem `default`: ação nova de consulta não compila sem a frase dela. */
function fraseDaConsulta(
  atividade: Extract<AtividadeDaOportunidade, { tipo: 'consulta' }>,
): string {
  switch (atividade.acao) {
    case 'marcada':
      return 'Consulta marcada no SISAC';
    case 'cancelada':
      return 'Consulta cancelada no SISAC';
    case 'escolhida':
      return atividade.campos.length > 0
        ? `Consulta atualizada: ${juntar(atividade.campos.map((c) => NOME_DO_CAMPO_DA_CONSULTA[c]))}`
        : 'Consulta atualizada';
  }
}

function fraseDaAtividade(
  atividade: AtividadeDaOportunidade,
): Pick<EntradaDoHistorico, 'grupo' | 'frase' | 'detalhes'> {
  switch (atividade.tipo) {
    case 'nota':
      return {
        grupo: 'nota',
        frase: atividade.acao === 'escrita' ? 'Nota adicionada' : 'Nota corrigida',
        detalhes: [atividade.trecho ? `“${atividade.trecho}”` : 'A nota foi apagada depois.'],
      };
    case 'documento':
      return { grupo: 'documento', ...fraseDoDocumento(atividade) };
    case 'consulta':
      return { grupo: 'consulta', frase: fraseDaConsulta(atividade), detalhes: [] };
    case 'edicao':
      return {
        grupo: 'edicao',
        frase: 'Oportunidade editada',
        detalhes: atividade.mudancas.map(linhaDaMudanca),
      };
  }
}

function entradaDaAtividade(atividade: AtividadeDaOportunidade, i: number): EntradaDoHistorico {
  return {
    chave: `atividade-${atividade.quando}-${i}`,
    quando: atividade.quando,
    ...fraseDaAtividade(atividade),
    por: atividade.por ? `por ${atividade.por}` : 'pelo sistema',
    resultado: null,
  };
}

/**
 * O passo que a rotina trouxe do SISAC (#1151): a consulta ou o exame, quando
 * chegou ao card, com o médico, as datas e o valor.
 */
function entradaDoAtendimento(atendimento: AtendimentoDoSisac): EntradaDoHistorico {
  const marcado = quandoNaClinica(atendimento.marcado_para);
  const agendado = quandoNaClinica(atendimento.agendado_em);
  const valor = dinheiroComCentavos(atendimento.valor);
  return {
    chave: `atendimento:${atendimento.referencia}`,
    quando: atendimento.ligado_em,
    grupo: 'consulta',
    frase: `${nomeDoAtendimento(atendimento)}, no SISAC`,
    detalhes: [
      ...(atendimento.medico ? [`Médico: ${atendimento.medico}`] : []),
      ...(marcado ? [`Marcado para ${marcado}`] : []),
      ...(agendado ? [`Agendado em ${agendado}`] : []),
      ...(valor ? [`${valorEhPrevisto(atendimento) ? 'Valor previsto' : 'Valor'}: ${valor}`] : []),
      ...(atendimento.servico_da_observacao ? ['Exame anotado pela recepção na agenda'] : []),
    ],
    por: 'pela rotina do funil',
    resultado: null,
  };
}

/** Tudo o que aconteceu na oportunidade, do mais recente ao mais antigo. */
export function linhaDoTempo(
  passos: readonly PassoNoFunil[],
  atividades: readonly AtividadeDaOportunidade[],
  atendimentos: readonly AtendimentoDoSisac[] = [],
): EntradaDoHistorico[] {
  const entradas = [
    ...passos.map(entradaDoPasso),
    ...atividades.map(entradaDaAtividade),
    ...atendimentos.map(entradaDoAtendimento),
  ];
  // Estável: no mesmo instante, a ordem de chegada se mantém.
  return entradas
    .map((entrada, posicao) => ({ entrada, posicao }))
    .sort((a, b) => b.entrada.quando.localeCompare(a.entrada.quando) || a.posicao - b.posicao)
    .map(({ entrada }) => entrada);
}
